import { useState, useEffect } from "react";
import { ScrollView, Pressable, ActivityIndicator, View, Text } from "react-native";
import styled from "styled-components/native";
import { useLocalSearchParams, useRouter } from "expo-router";
import { CloseCircleIcon, TrashIcon } from "@/components/Icons";
import { doc, onSnapshot, collection, query, orderBy, setDoc, updateDoc, deleteDoc, increment, Timestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { useConfirm } from "@/hooks/useConfirm";
import { useAlert } from "@/hooks/useAlert";
import ConfirmSheet from "@/components/ConfirmSheet";
import AlertSheet from "@/components/AlertSheet";
import { colors, radius, font } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";
import type { ClubEvent, EventResponse, EventResponseStatus } from "@/lib/types";

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return dateStr; }
}

function formatTime(timeStr: string): string {
  try {
    const [h, m] = timeStr.split(":");
    const hour = parseInt(h);
    if (hour >= 12) return `${hour === 12 ? 12 : hour - 12}:${m}pm`;
    return `${hour === 0 ? 12 : hour}:${m}am`;
  } catch { return timeStr; }
}

const Screen = styled.ScrollView`flex: 1; background-color: ${colors.bg};`;
const Center = styled.View`flex: 1; background-color: ${colors.bg}; justify-content: center; align-items: center; padding: 24px;`;

const EventHeader = styled.View`padding: 20px; border-bottom-width: 1px; border-bottom-color: ${colors.line}; background-color: ${colors.white};`;
const Title = styled.Text`font-size: 22px; font-weight: ${font.extrabold}; color: ${colors.ink}; letter-spacing: -0.5px;`;
const MetaRow = styled.View`flex-direction: row; align-items: center; gap: 6px; margin-top: 8px;`;
const MetaText = styled.Text`font-size: 14px; color: ${colors.inkSecondary}; font-weight: ${font.medium};`;
const MetaIcon = styled.Text`font-size: 14px;`;

const BadgeRow = styled.View`flex-direction: row; gap: 8px; margin-top: 12px;`;
const Badge = styled.View<{ bg: string; border: string }>`padding: 4px 12px; border-radius: 999px; background-color: ${(p) => p.bg}; border: 1px solid ${(p) => p.border};`;
const BadgeText = styled.Text<{ color: string }>`font-size: 12px; font-weight: ${font.bold}; color: ${(p) => p.color};`;

const Section = styled.View`padding: 20px;`;
const SectionLabel = styled.Text`font-size: 12px; font-weight: ${font.extrabold}; color: ${colors.inkMuted}; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 12px;`;
const DescText = styled.Text`font-size: 15px; color: ${colors.ink}; line-height: 24px;`;

const FeeCard = styled.View`background-color: ${colors.amber50}; border-radius: ${radius.md}px; border: 1px solid ${colors.amber200}; padding: 14px; align-items: center;`;
const FeeAmount = styled.Text`font-size: 24px; font-weight: ${font.extrabold}; color: ${colors.amber};`;
const FeeSub = styled.Text`font-size: 13px; color: ${colors.amber}; font-weight: ${font.medium}; margin-top: 2px;`;

const RsvpBtns = styled.View`flex-direction: row; gap: 10px;`;
const RsvpBtn = styled.View<{ isSelected: boolean; bg: string; borderClr: string }>`
  flex: 1; align-items: center; justify-content: center; padding: 14px 8px;
  border-radius: ${radius.md}px; border-width: ${(p) => p.isSelected ? "2px" : "1px"};
  background-color: ${(p) => p.isSelected ? p.bg : colors.white};
  border-color: ${(p) => p.isSelected ? p.borderClr : colors.line};
`;
const RsvpEmoji = styled.Text`font-size: 24px;`;

const PayBtn = styled.View`background-color: ${colors.brand}; border-radius: ${radius.md}px; padding: 14px; align-items: center; margin-top: 12px;`;
const PayBtnText = styled.Text`color: #fff; font-size: 15px; font-weight: ${font.bold};`;

const ResponseCard = styled.View`background-color: ${colors.white}; border-radius: ${radius.lg}px; border: 1px solid ${colors.line}; overflow: hidden;`;
const ResponseRow = styled.View<{ bordered?: boolean }>`
  flex-direction: row; align-items: center; padding: 12px 14px;
  ${(p) => p.bordered ? `border-bottom-width: 1px; border-bottom-color: ${colors.line};` : ""}
`;
const Avatar = styled.View<{ bg: string }>`width: 36px; height: 36px; border-radius: 18px; background-color: ${(p) => p.bg}; align-items: center; justify-content: center; margin-right: 10px;`;
const AvatarText = styled.Text<{ color: string }>`font-size: 13px; font-weight: ${font.extrabold}; color: ${(p) => p.color};`;
const ResponseName = styled.Text`font-size: 14px; font-weight: ${font.bold}; color: ${colors.ink};`;
const StatusEmoji = styled.Text`font-size: 16px; margin-left: auto;`;

const NotInvited = styled.View`padding: 40px; align-items: center;`;
const NotInvitedText = styled.Text`font-size: 15px; color: ${colors.inkMuted}; text-align: center;`;

const EmptyText = styled.Text`font-size: 13px; color: ${colors.inkMuted}; padding: 8px 0;`;
const Spacer = styled.View`height: 32px;`;

const rsvpConfig: Record<EventResponseStatus, { emoji: string; bg: string; border: string }> = {
  going: { emoji: "✅", bg: colors.brand50, border: colors.brandLight },
  "not-going": { emoji: "❌", bg: colors.dangerLight, border: "#fecaca" },
  maybe: { emoji: "🤔", bg: colors.amber50, border: colors.amber200 },
};

export default function EventDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const { user } = useAuth();
  const { club, membership } = useClub();

  const [event, setEvent] = useState<(ClubEvent & { id: string }) | null>(null);
  const [responses, setResponses] = useState<(EventResponse & { id: string })[]>([]);
  const [myResponse, setMyResponse] = useState<EventResponseStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { confirmProps, confirm } = useConfirm();
  const { alertProps, alert } = useAlert();

  const canManage = membership?.role === "admin" || (event && event.createdBy === user?.uid);

  const handleDeleteEvent = async () => {
    if (!club || !id) return;
    const ok = await confirm({
      title: "Delete event",
      message: "This event will be permanently deleted.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await deleteDoc(doc(db, "clubs", club.id, "events", id));
    router.back();
  };

  const handleCancelEvent = async () => {
    if (!club || !id || !event) return;
    const ok = await confirm({
      title: "Cancel event",
      message: `Mark "${event.title}" as cancelled?`,
      confirmLabel: "Cancel event",
      destructive: true,
    });
    if (!ok) return;
    await updateDoc(doc(db, "clubs", club.id, "events", id), { cancelled: true });
    await alert({ title: "Event cancelled", emoji: "🚫", message: "This event has been cancelled." });
  };

  useEffect(() => {
    if (!club?.id || !id) return;
    const unsub = onSnapshot(doc(db, "clubs", club.id, "events", id), (snap) => {
      if (snap.exists()) setEvent({ id: snap.id, ...snap.data() } as any);
      setLoading(false);
    });
    return unsub;
  }, [club?.id, id]);

  useEffect(() => {
    if (!club?.id || !id) return;
    const unsub = onSnapshot(
      query(collection(db, "clubs", club.id, "events", id, "responses"), orderBy("respondedAt", "desc")),
      (snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as (EventResponse & { id: string })[];
        setResponses(items);
        if (user) {
          const mine = items.find((r) => r.userId === user.uid);
          setMyResponse(mine?.status || null);
        }
      }
    );
    return unsub;
  }, [club?.id, id, user?.uid]);

  const handleRsvp = async (status: EventResponseStatus) => {
    if (!club || !user || !membership || !id || !event) return;
    setSaving(true);

    const prevStatus = myResponse;
    const respRef = doc(db, "clubs", club.id, "events", id, "responses", user.uid);
    const eventRef = doc(db, "clubs", club.id, "events", id);

    await setDoc(respRef, {
      userId: user.uid,
      displayName: membership.displayName,
      status,
      respondedAt: Timestamp.now(),
    });

    // Update counts
    const updates: any = {};
    if (prevStatus === "going") updates.goingCount = increment(-1);
    else if (prevStatus === "not-going") updates.notGoingCount = increment(-1);
    else if (prevStatus === "maybe") updates.maybeCount = increment(-1);

    if (status === "going") updates.goingCount = increment(1);
    else if (status === "not-going") updates.notGoingCount = increment(1);
    else if (status === "maybe") updates.maybeCount = increment(1);

    if (Object.keys(updates).length > 0) {
      await updateDoc(eventRef, updates);
    }

    setSaving(false);
  };

  if (loading) return <Center><ActivityIndicator size="large" color={colors.brand} /></Center>;
  if (!event) return <Center><NotInvitedText>Event not found.</NotInvitedText></Center>;

  // Check invite-only access
  const isInvited = event.type === "open" ||
    event.invitedMembers.includes(user?.uid || "") ||
    membership?.role === "admin";

  if (!isInvited) {
    return (
      <Center>
        <NotInvited>
          <NotInvitedText>🔒 This is an invite-only event.{"\n"}You haven't been invited.</NotInvitedText>
        </NotInvited>
      </Center>
    );
  }

  const going = responses.filter((r) => r.status === "going");
  const maybe = responses.filter((r) => r.status === "maybe");
  const notGoing = responses.filter((r) => r.status === "not-going");

  return (
    <Screen showsVerticalScrollIndicator={false}>
      <ContentWidth>
        <EventHeader>
          <Title>{event.title}</Title>
          <MetaRow><MetaIcon>📅</MetaIcon><MetaText>{formatDate(event.date)} · {formatTime(event.time)}</MetaText></MetaRow>
          <MetaRow><MetaIcon>📍</MetaIcon><MetaText>{event.location}</MetaText></MetaRow>
          <BadgeRow>
            <Badge bg={event.type === "open" ? colors.brand50 : colors.amber50} border={event.type === "open" ? colors.brandLight : colors.amber200}>
              <BadgeText color={event.type === "open" ? colors.brandDark : colors.amber}>{event.type === "open" ? "Open" : "Invite only"}</BadgeText>
            </Badge>
            {event.isPaid && (
              <Badge bg={colors.amber50} border={colors.amber200}>
                <BadgeText color={colors.amber}>£{(event.fee / 100).toFixed(0)} per person</BadgeText>
              </Badge>
            )}
          </BadgeRow>
          {canManage && (
            <View style={{ flexDirection: "row", gap: 10, marginTop: 12 }}>
              <Pressable
                onPress={handleCancelEvent}
                style={({ pressed }) => ({
                  width: 40, height: 40, borderRadius: 20,
                  backgroundColor: colors.amber50, borderWidth: 1, borderColor: colors.amber200,
                  alignItems: "center", justifyContent: "center",
                  opacity: pressed ? 0.7 : 1,
                })}
              >
                <CloseCircleIcon size={18} color={colors.amber} />
              </Pressable>
              <Pressable
                onPress={handleDeleteEvent}
                style={({ pressed }) => ({
                  width: 40, height: 40, borderRadius: 20,
                  backgroundColor: colors.dangerLight, borderWidth: 1, borderColor: "#fecaca",
                  alignItems: "center", justifyContent: "center",
                  opacity: pressed ? 0.7 : 1,
                })}
              >
                <TrashIcon size={18} color={colors.danger} />
              </Pressable>
            </View>
          )}
        </EventHeader>

        {/* Description */}
        {event.description ? (
          <Section>
            <SectionLabel>About</SectionLabel>
            <DescText>{event.description}</DescText>
          </Section>
        ) : null}

        {/* Fee */}
        {event.isPaid && event.fee > 0 && (
          <Section>
            <FeeCard>
              <FeeAmount>£{(event.fee / 100).toFixed(0)}</FeeAmount>
              <FeeSub>per person</FeeSub>
            </FeeCard>
            {myResponse === "going" && (
              <Pressable onPress={() => router.push(`/(app)/pay?amount=${event.fee}&description=${encodeURIComponent(event.title)}&feeType=event`)}>
                <PayBtn><PayBtnText>Pay £{(event.fee / 100).toFixed(0)}</PayBtnText></PayBtn>
              </Pressable>
            )}
          </Section>
        )}

        {/* RSVP */}
        <Section>
          <SectionLabel>Your RSVP</SectionLabel>
          <RsvpBtns>
            {(["going", "not-going", "maybe"] as EventResponseStatus[]).map((status) => {
              const config = rsvpConfig[status];
              const isSelected = myResponse === status;
              return (
                <Pressable key={status} onPress={() => handleRsvp(status)} disabled={saving} style={{ flex: 1 }}>
                  <RsvpBtn isSelected={isSelected} bg={config.bg} borderClr={config.border}>
                    <RsvpEmoji>{config.emoji}</RsvpEmoji>
                  </RsvpBtn>
                </Pressable>
              );
            })}
          </RsvpBtns>
        </Section>

        {/* Attendees */}
        {going.length > 0 && (
          <Section>
            <SectionLabel>Going ({going.length})</SectionLabel>
            <ResponseCard>
              {going.map((r, i) => (
                <ResponseRow key={r.id} bordered={i < going.length - 1}>
                  <Avatar bg={colors.brand50}><AvatarText color={colors.brandDark}>{r.displayName?.[0]?.toUpperCase()}</AvatarText></Avatar>
                  <ResponseName>{r.displayName}</ResponseName>
                  <StatusEmoji>✅</StatusEmoji>
                </ResponseRow>
              ))}
            </ResponseCard>
          </Section>
        )}

        {maybe.length > 0 && (
          <Section>
            <SectionLabel>Maybe ({maybe.length})</SectionLabel>
            <ResponseCard>
              {maybe.map((r, i) => (
                <ResponseRow key={r.id} bordered={i < maybe.length - 1}>
                  <Avatar bg={colors.amber50}><AvatarText color={colors.amber}>{r.displayName?.[0]?.toUpperCase()}</AvatarText></Avatar>
                  <ResponseName>{r.displayName}</ResponseName>
                  <StatusEmoji>🤔</StatusEmoji>
                </ResponseRow>
              ))}
            </ResponseCard>
          </Section>
        )}

        {notGoing.length > 0 && (
          <Section>
            <SectionLabel>Not going ({notGoing.length})</SectionLabel>
            <ResponseCard>
              {notGoing.map((r, i) => (
                <ResponseRow key={r.id} bordered={i < notGoing.length - 1}>
                  <Avatar bg={colors.dangerLight}><AvatarText color={colors.danger}>{r.displayName?.[0]?.toUpperCase()}</AvatarText></Avatar>
                  <ResponseName>{r.displayName}</ResponseName>
                  <StatusEmoji>❌</StatusEmoji>
                </ResponseRow>
              ))}
            </ResponseCard>
          </Section>
        )}

        <Spacer />
      </ContentWidth>
      <ConfirmSheet {...confirmProps} />
      <AlertSheet {...alertProps} />
    </Screen>
  );
}
